// Выбор темы пользователем (FR-SET-03). Хранится локально (localStorage) и применяется через
// design-system ThemeProvider; 'system' переключается на лету самим ThemeProvider по prefers-color-scheme.
// Это UI-настройка (не доменное состояние) — её владелец app-ui.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { ThemeProvider, type ThemePreference } from '@tele-chat/design-system'

const STORAGE_KEY = 'tele-chat:theme'

export interface ThemePreferenceValue {
  preference: ThemePreference
  setPreference(p: ThemePreference): void
}

const ThemePreferenceContext = createContext<ThemePreferenceValue | null>(null)

function readStored(): ThemePreference {
  if (typeof localStorage === 'undefined') return 'system'
  const v = localStorage.getItem(STORAGE_KEY)
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system'
}

export interface AppThemeProviderProps {
  children: ReactNode
  /** Принудительная тема (тесты) — игнорирует localStorage. */
  initial?: ThemePreference
}

/** Объединяет выбор темы (state+persist) с design-system ThemeProvider (применяет CSS-vars). */
export function AppThemeProvider({ children, initial }: AppThemeProviderProps): JSX.Element {
  const [preference, setPreferenceState] = useState<ThemePreference>(initial ?? readStored)

  const setPreference = useCallback((p: ThemePreference): void => {
    setPreferenceState(p)
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, p)
  }, [])

  const value = useMemo<ThemePreferenceValue>(
    () => ({ preference, setPreference }),
    [preference, setPreference],
  )

  return (
    <ThemePreferenceContext.Provider value={value}>
      <ThemeProvider theme={preference} style={{ height: '100%' }}>
        {children}
      </ThemeProvider>
    </ThemePreferenceContext.Provider>
  )
}

export function useThemePreference(): ThemePreferenceValue {
  const ctx = useContext(ThemePreferenceContext)
  if (ctx === null) {
    throw new Error('useThemePreference(): компонент должен быть внутри <AppThemeProvider>')
  }
  return ctx
}
